import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { getApiUrl } from "@/lib/config";
import { AppLayout } from "@/components/app-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { useState, useEffect } from "react";
import { 
  MessageSquare, 
  Send,
  Mail,
  Clock,
  Loader2,
  Paperclip
} from "lucide-react";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Help() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [user, setUser] = useState<any>(null);
  const [ticketTitle, setTicketTitle] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) {
      setLocation("/login");
      return;
    }
    const userData = JSON.parse(stored);
    setUser(userData);
  }, [setLocation]);

  const submitTicketMutation = useMutation({
    mutationFn: async (data: { 
      title: string; 
      description: string; 
      requesterName: string;
      requesterEmail: string;
      attachment?: { name: string; content: string; type: string };
    }) => {
      const res = await fetch(getApiUrl("/api/support/ticket"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error("Failed to submit");
      return res.json();
    },
    onSuccess: () => {
      toast({ 
        title: t("ui.support_request_sent"), 
        description: t("ui.we_ll_get_back_to_you_as_soon_as_possible") 
      });
      setTicketTitle("");
      setTicketDescription("");
      setRequesterName("");
      setRequesterEmail("");
      setAttachment(null);
    },
    onError: () => {
      toast({ 
        title: t("ui.failed_to_send_request"), 
        description: t("ui.please_try_again_later"),
        variant: "destructive" 
      });
    }
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocation("/");
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmitTicket = async () => {
    if (!ticketTitle.trim() || !ticketDescription.trim() || !requesterName.trim() || !requesterEmail.trim()) {
      toast({ title: t("ui.please_fill_in_all_required_fields"), variant: "destructive" });
      return;
    }
    if (!isValidEmail(requesterEmail)) {
      toast({ title: t("ui.please_enter_a_valid_email_address"), variant: "destructive" });
      return;
    }
    
    let attachmentData: { name: string; content: string; type: string } | undefined;
    if (attachment) {
      const base64Content = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(attachment);
      });
      attachmentData = {
        name: attachment.name,
        content: base64Content,
        type: attachment.type
      };
    }
    
    submitTicketMutation.mutate({ 
      title: ticketTitle, 
      description: ticketDescription,
      requesterName,
      requesterEmail,
      attachment: attachmentData
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0]);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("ui.loading")}</div>
      </div>
    );
  }


  return (
    <AppLayout user={user} onLogout={handleLogout} plan={user.plan} currentPage="/help">
      <div className="space-y-6 max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="text-page-title">{t("help.title") || "Help & Support"}</h1>
          <p className="text-muted-foreground mt-1">{t("help.subtitle") || "Get help with FillSign"}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {t("help.contactSupport") || "Contact Support"}
            </CardTitle>
            <CardDescription>
              {t("help.contactDesc") || "Fill out the form below and we'll get back to you as soon as possible"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4 p-4 bg-muted/30 rounded-md">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">1</div>
                <div>
                  <p className="font-medium">{t("help.step1Title") || "Fill out the form"}</p>
                  <p className="text-sm text-muted-foreground">{t("help.step1Desc") || "Provide your details and describe your issue below"}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">2</div>
                <div>
                  <p className="font-medium">{t("help.step2Title") || "Submit your request"}</p>
                  <p className="text-sm text-muted-foreground">{t("help.step2Desc") || "Click the submit button to send your support request"}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">3</div>
                <div>
                  <p className="font-medium">{t("help.step3Title")}</p>
                  <p className="text-sm text-muted-foreground">{t("ui.our_team_will_review_and_respond_to_your_email_w")}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="requesterName">{t("ui.your_name")}</Label>
                  <Input
                    id="requesterName"
                    placeholder="John Doe"
                    value={requesterName}
                    onChange={(e) => setRequesterName(e.target.value)}
                    data-testid="input-requester-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="requesterEmail">{t("ui.your_email")}</Label>
                  <Input
                    id="requesterEmail"
                    type="email"
                    placeholder="john@example.com"
                    value={requesterEmail}
                    onChange={(e) => setRequesterEmail(e.target.value)}
                    data-testid="input-requester-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ticketTitle">{t("ui.title")}</Label>
                <Input
                  id="ticketTitle"
                  placeholder={t("ui.brief_summary_of_your_issue")}
                  value={ticketTitle}
                  onChange={(e) => setTicketTitle(e.target.value)}
                  data-testid="input-ticket-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ticketDescription">{t("ui.description")}</Label>
                <Textarea
                  id="ticketDescription"
                  placeholder={t("ui.please_describe_your_issue_in_detail")}
                  rows={5}
                  value={ticketDescription}
                  onChange={(e) => setTicketDescription(e.target.value)}
                  data-testid="input-ticket-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="attachment">{t("ui.attachment_optional")}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="attachment"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="input-attachment"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById("attachment")?.click()}
                    data-testid="button-attach-file"
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    {attachment ? attachment.name : "Attach a file"}
                  </Button>
                  {attachment && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAttachment(null)}
                      data-testid="button-remove-attachment"
                    >
                      {t("ui.remove")}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{t("ui.max_file_size_10mb_supported_pdf_png_jpg_docx")}</p>
              </div>

              <Button 
                onClick={handleSubmitTicket} 
                disabled={submitTicketMutation.isPending}
                className="w-full"
                data-testid="button-submit-ticket"
              >
                {submitTicketMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("ui.sending")}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t("ui.submit_support_request")}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {t("ui.direct_contact")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You can also reach us directly at{" "}
              <a href="mailto:tulio.koca@gmail.com,larissaferreirasantos58@gmail.com" className="text-primary hover:underline" data-testid="link-support-email">
                support@fillsign.com.br
              </a>
            </p>
            <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{t("ui.response_time_typically_within_24_48_hours")}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
